import type { HandlingUnitHashMap, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";

export type PackingListFieldRulesContext = {
  nmfcNumberEnabled?: string | null;
};

export type PackingListWorkflowColumnFlags = {
  hazardousEnabled: boolean;
  temperatureEnabled: boolean;
  dangerousEnabled: boolean;
  cratingEnabled: boolean;
  nmfcEnabled: boolean;
};

type PackingListWorkflowConfigSlice = {
  hazardous_goods_enabled?: boolean;
  temperature_controlled_enabled?: boolean;
  dangerous_goods_enabled?: boolean;
  crating_required_enabled?: boolean;
  nmfc_number_enabled?: string | null;
};

export function normalizePackingListWorkflowSetting(value?: string | null): string {
  return (value ?? "off").trim().toLowerCase();
}

export function isPackingListWorkflowSettingEnabled(value?: string | null): boolean {
  return normalizePackingListWorkflowSetting(value) !== "off";
}

export function isPackingListWorkflowSettingMandatory(value?: string | null): boolean {
  return normalizePackingListWorkflowSetting(value) === "mandatory";
}

export function isLocationNmfcColumnEnabled(nmfcNumberEnabled?: string | null): boolean {
  return isPackingListWorkflowSettingEnabled(nmfcNumberEnabled);
}

export function isPackingListWorkflowConfigReady(location: string | undefined, isLoadingPackingListConfig: boolean): boolean {
  return !location || !isLoadingPackingListConfig;
}

export function getPackingListWorkflowColumnFlags(
  packingListConfig?: PackingListWorkflowConfigSlice | null,
): PackingListWorkflowColumnFlags {
  return {
    hazardousEnabled: Boolean(packingListConfig?.hazardous_goods_enabled),
    temperatureEnabled: Boolean(packingListConfig?.temperature_controlled_enabled),
    dangerousEnabled: Boolean(packingListConfig?.dangerous_goods_enabled),
    cratingEnabled: Boolean(packingListConfig?.crating_required_enabled),
    nmfcEnabled: isLocationNmfcColumnEnabled(packingListConfig?.nmfc_number_enabled),
  };
}

function isLocationSettingsNmfc(huNmfc?: string | null): boolean {
  return normalizePackingListWorkflowSetting(huNmfc) === "location settings";
}

export function getEffectiveNmfcSetting(
  row: PackingListRow,
  handlingUnitHashMap: HandlingUnitHashMap,
  nmfcNumberEnabled?: string | null,
): string | undefined {
  if (isContainerRow(row)) {
    return undefined;
  }

  const key = row.handling_unit?.toLowerCase();
  if (!key) {
    return undefined;
  }

  const settings = handlingUnitHashMap[key];
  if (!settings) {
    return undefined;
  }

  if (isLocationSettingsNmfc(settings.nmfc)) {
    return nmfcNumberEnabled ?? "off";
  }

  return settings.nmfc;
}

export function isNmfcInputVisibleForRow(
  row: PackingListRow,
  handlingUnitHashMap: HandlingUnitHashMap,
  nmfcNumberEnabled?: string | null,
): boolean {
  if (!isLocationNmfcColumnEnabled(nmfcNumberEnabled)) {
    return false;
  }

  return isPackingListWorkflowSettingEnabled(getEffectiveNmfcSetting(row, handlingUnitHashMap, nmfcNumberEnabled));
}

export function isNmfcFieldRequiredForRow(
  row: PackingListRow,
  handlingUnitHashMap: HandlingUnitHashMap,
  nmfcNumberEnabled?: string | null,
): boolean {
  if (!isLocationNmfcColumnEnabled(nmfcNumberEnabled)) {
    return false;
  }

  const effective = getEffectiveNmfcSetting(row, handlingUnitHashMap, nmfcNumberEnabled);
  if (!isPackingListWorkflowSettingEnabled(effective)) {
    return false;
  }

  return isPackingListWorkflowSettingMandatory(effective);
}
